import { serverTimestamp } from 'firebase/firestore';
import {
  formatLocalTimeForStorage,
  normalizeImportCategory,
  normalizeTextForImport,
} from './importLogsParsing';
import { buildNaturalCaptureCategoryDecision } from './naturalCaptureCategoryCore';
import { buildConciseBehaviorTitle } from '../../../utils/behaviorTitles';
import { buildConciseMilestoneTitle } from '../../../utils/milestoneTitles';
import { buildCareEntryTitle } from '../../../shared/careEntryTitles';
import { buildImportFoodHeadline } from '../../../shared/careFoodTitles';

const normalizeSourceObject = (source = {}) => (
  source && typeof source === 'object' && !Array.isArray(source) ? source : {}
);

const capitalizeFirst = (value = '') => {
  const text = String(value || '').trim();
  if (!text) {
    return '';
  }

  return `${text[0].toUpperCase()}${text.slice(1)}`;
};

const inferBehaviorSubtype = (text = '') => {
  const comparable = normalizeTextForImport(text);

  if (/(meltdown|tantrum|crisis)/i.test(comparable)) return 'meltdown';
  if (/(frustrated|upset|angry|cranky|irritable)/i.test(comparable)) return 'dysregulation';
  if (/(aggressive|aggression|hit|slapped?|pushed|kicked|eloped|elopement)/i.test(comparable)) return 'aggression';
  if (/(refused|school refusal|would not|wouldn't|did not want|didn’t want)/i.test(comparable)) return 'refusal';
  if (/(needed a break|break to calm down|quiet space|calm down)/i.test(comparable)) return 'escalation';

  return 'behavior';
};

const getBehaviorSeverityDefaults = (text = '') => {
  const comparable = normalizeTextForImport(text);
  const highConcern = /(?:crisis|safety concern|significant|major concern|aggression|elopement|required support|needed a break)/i.test(comparable);
  return {
    severity: highConcern ? 'high' : 'moderate',
    severityLabel: highConcern ? 'High' : 'Moderate',
  };
};

const buildBehaviorHeadline = (value = '') => {
  const text = normalizeTextForImport(value);
  if (!text) return 'Behavior note';
  return buildConciseBehaviorTitle(text);
};

const buildFoodHeadline = (value = '') => {
  const text = normalizeTextForImport(value);
  return buildImportFoodHeadline(text);
};

const buildMedicationHeadline = (value = '') => {
  const text = normalizeTextForImport(value);
  return buildCareEntryTitle('medication', text);
};

const buildSleepHeadline = (value = '') => {
  const text = normalizeTextForImport(value);
  if (!text) return 'Sleep';

  const lowerText = text.toLowerCase();
  const qualityMatch = lowerText.match(/\b(good|great|poor|fair|awful|restless|settled|in progress)\b/i);
  if (qualityMatch?.[1]) return `${capitalizeFirst(qualityMatch[1].toLowerCase())} sleep`;
  if (/\bwoke(?:\s+once|\s+multiple times?)?\b/i.test(lowerText)) return 'Woke during sleep';

  return 'Sleep logged';
};

const buildMilestoneHeadline = (value = '') => {
  const text = normalizeTextForImport(value);
  return buildConciseMilestoneTitle(text);
};

const buildMoodHeadline = (value = '') => {
  const text = normalizeTextForImport(value);
  if (!text) return 'Mood';

  const lowerText = text.toLowerCase();
  const moodMatch = lowerText.match(/\b(calm|happy|upset|frustrat(?:ed|ing)|cranky|irritable|angry|anxious|sad|neutral|excited|tired|content|worried)\b/i);
  if (moodMatch?.[1]) return capitalizeFirst(moodMatch[1].toLowerCase());

  return 'Mood';
};

const isCaptureFirstPreservedRow = ({
  row = {},
  docData = {},
  sourceObject = {},
  sourceMode = '',
  inputMode = '',
  parserMode = '',
} = {}) => {
  const metadata = {
    ...(docData?.metadata && typeof docData.metadata === 'object' ? docData.metadata : {}),
    ...(row?.metadata && typeof row.metadata === 'object' ? row.metadata : {}),
  };

  return Boolean(
    metadata.captureFirst
    || metadata.inputMode === 'capture_first'
    || metadata.sourceMode === 'quick_log_capture_first'
    || row?.inputMode === 'capture_first'
    || row?.sourceMode === 'quick_log_capture_first'
    || sourceObject.mode === 'capture_first'
    || sourceObject.type === 'quick_log_capture_first'
    || inputMode === 'capture_first'
    || sourceMode === 'quick_log_capture_first'
    || parserMode === 'capture_first_backend_ai'
  );
};

const buildParsedSummaryTitle = (category = 'log', value = '') => {
  const normalizedCategory = normalizeImportCategory(category);
  const text = normalizeTextForImport(value);
  const sourceCategory = String(category || '').trim().toLowerCase();

  switch (sourceCategory === 'note' ? 'note' : normalizedCategory) {
    case 'behavior': return buildBehaviorHeadline(text);
    case 'food': return buildFoodHeadline(text);
    case 'medication': return buildMedicationHeadline(text);
    case 'sleep': return buildSleepHeadline(text);
    case 'milestone': return buildMilestoneHeadline(text);
    case 'note': return text.split(/(?<=[.!?])\s+/)[0]?.trim() || 'Note';
    case 'mood': return buildMoodHeadline(text);
    case 'activity': return 'Activity';
    default:
      return text.split(/(?<=[.!?])\s+/)[0]?.trim() || 'Note';
  }
};

const isGenericOrSentenceLikeTitle = (title = '', category = '', text = '') => {
  const normalizedTitle = normalizeTextForImport(title);
  const normalizedText = normalizeTextForImport(text);
  const normalizedCategory = normalizeImportCategory(category);

  if (!normalizedTitle) {
    return true;
  }

  if (normalizedTitle === normalizedText) {
    return true;
  }

  if (
    normalizedTitle === normalizedCategory
    || normalizedTitle === 'log'
    || normalizedTitle === 'note'
    || normalizedTitle === 'daily log'
    || normalizedTitle === 'behavior note'
    || normalizedTitle === 'medication'
    || normalizedTitle === 'food'
    || normalizedTitle === 'sleep'
    || normalizedTitle === 'activity'
    || normalizedTitle === 'mood'
  ) {
    return true;
  }

  const titleWordCount = normalizedTitle.split(/\s+/).filter(Boolean).length;
  return titleWordCount >= 10 || normalizedTitle.length >= 90;
};

export const buildBehaviorImportDocData = (row = {}, docData = {}) => {
  const originalText = String(row?.note || row?.text || docData?.sourceText || docData?.originalText || docData?.text || '').trim();
  const noteText = originalText;
  const subtype = inferBehaviorSubtype(noteText);
  const { severity, severityLabel } = getBehaviorSeverityDefaults(noteText);
  const parsedObservation = Boolean(row?.parsedObservation ?? docData?.parsedObservation);
  const resolvedCategory = normalizeImportCategory(row?.category || docData?.category || 'behavior');
  const category = resolvedCategory === 'log' ? 'behavior' : resolvedCategory;
  const titleSource = String(
    row?.title
    || docData?.title
    || row?.titlePrefix
    || docData?.titlePrefix
    || noteText
    || docData?.text
    || 'Behavior incident'
  ).trim();
  const title = buildBehaviorHeadline(titleSource);
  const titlePrefix = buildBehaviorHeadline(
    row?.titlePrefix
    || docData?.titlePrefix
    || titleSource
  );

  return {
    ...docData,
    collection: docData.collection || 'dailyLogs',
    status: docData.status || 'active',
    createdBy: docData.createdBy || null,
    createdAt: docData.createdAt || serverTimestamp(),
    updatedAt: docData.updatedAt || serverTimestamp(),
    timestamp: docData.timestamp || row?.date || new Date(),
    entryDate: docData.entryDate || docData.entryDateLabel || null,
    entryDayKey: docData.entryDayKey || null,
    category,
    type: category,
    logCategory: category,
    timelineType: 'incident',
    entryType: 'incident',
    incidentStyle: true,
    incidentCategoryId: 'behavior',
    incidentCategoryLabel: 'Behavior',
    incidentCategoryColor: '#E099B6',
    title,
    titlePrefix,
    text: noteText || docData.text || 'Behavior incident',
    content: noteText || docData.content || 'Behavior incident',
    note: noteText || docData.note || 'Behavior incident',
    notes: noteText || docData.notes || 'Behavior incident',
    sourceText: noteText || docData.sourceText || docData.text || 'Behavior incident',
    originalText: noteText || docData.originalText || docData.text || 'Behavior incident',
    triggerSummary: docData.triggerSummary || noteText || null,
    severity,
    severityLabel,
    subtype,
    behaviorSubtype: subtype,
    incidentData: {
      ...(docData.incidentData || {}),
      severity,
      severityLabel,
      subtype,
      notes: noteText || docData.notes || 'Behavior incident',
      description: noteText || docData.text || 'Behavior incident',
      triggerSummary: docData.triggerSummary || noteText || null,
    },
    source: {
      ...normalizeSourceObject(docData.source),
      type: docData.source?.type || 'import',
      extractedBy: docData.source?.extractedBy || 'parser',
      confidence: docData.source?.confidence || 'low',
      parsedSourceCategory: 'behavior',
      parsedObservation,
    },
  };
};

export const buildBehaviorNoteFallbackDocData = (docData = {}) => {
  const fallbackText = String(docData?.originalText || docData?.sourceText || docData?.note || docData?.notes || docData?.content || docData?.text || '').trim();
  return buildBehaviorImportDocData(
    {
      ...docData,
      category: 'behavior',
      note: fallbackText || docData?.note || docData?.text || 'Behavior incident',
      text: fallbackText || docData?.text || 'Behavior incident',
      parsedObservation: false,
    },
    {
      ...docData,
      category: 'behavior',
      note: fallbackText || docData?.note || docData?.text || 'Behavior incident',
      text: fallbackText || docData?.text || 'Behavior incident',
      type: 'behavior',
      logCategory: 'behavior',
      timelineType: 'incident',
      entryType: 'incident',
      incidentStyle: true,
      incidentCategoryId: 'behavior',
      incidentCategoryLabel: 'Behavior',
      incidentCategoryColor: '#E099B6',
    source: {
      ...normalizeSourceObject(docData.source),
      parsedObservation: false,
    },
    }
  );
};

export const buildBehaviorObservationFallbackDocData = (docData = {}) => {
  const noteText = String(docData?.originalText || docData?.sourceText || docData?.note || docData?.notes || docData?.content || docData?.text || '').trim();
  const fallbackText = noteText || 'Behavior incident';
  const title = buildParsedSummaryTitle('behavior', fallbackText);

  return {
    ...docData,
    category: 'behavior',
    type: 'behavior',
    logCategory: 'behavior',
    timelineType: 'incident',
    entryType: 'incident',
    incidentStyle: true,
    title: docData.title || title,
    titlePrefix: docData.titlePrefix || title,
    text: noteText || fallbackText,
    content: noteText || fallbackText,
    note: noteText || fallbackText,
    notes: noteText || fallbackText,
    sourceText: noteText || docData.sourceText || docData.text || fallbackText,
    originalText: noteText || docData.originalText || docData.text || fallbackText,
    parsedSourceCategory: 'behavior',
    parsedSourceType: 'behavior',
    parsedObservation: true,
    parsedSourceConfidence: 'low',
    incidentCategoryId: 'behavior',
    incidentCategoryLabel: 'Behavior',
    incidentCategoryColor: '#E099B6',
    severity: docData.severity || 5,
    severityLabel: docData.severityLabel || 'Moderate',
    incidentData: {
      ...(docData.incidentData || {}),
      description: noteText || fallbackText,
      notes: noteText || fallbackText,
      severity: docData.severity || 5,
      severityLabel: docData.severityLabel || 'Moderate',
      subtype: docData.subtype || docData.behaviorSubtype || inferBehaviorSubtype(fallbackText),
    },
    source: {
      ...normalizeSourceObject(docData.source),
      type: docData.source?.type || 'import',
      extractedBy: docData.source?.extractedBy || 'parser',
      confidence: docData.source?.confidence || 'low',
      parsedSourceCategory: 'behavior',
      parsedObservation: true,
    },
  };
};

export const buildParsedObservationImportDocData = (row = {}, docData = {}, suggestedCategory = 'log') => {
  const originalText = String(row?.note || row?.text || docData?.sourceText || docData?.originalText || docData?.text || '').trim();
  const fallbackText = originalText || docData?.text || 'Note';
  const title = buildParsedSummaryTitle(suggestedCategory, fallbackText);
  const parsedSourceConfidence = row?.reviewConfidence || docData?.source?.confidence || 'low';
  const sourceCategory = String(row?.category || docData?.category || suggestedCategory || 'log').trim().toLowerCase();
  const resolvedCategory = normalizeImportCategory(sourceCategory);
  const category = resolvedCategory === 'log' ? normalizeImportCategory(suggestedCategory) : resolvedCategory;
  const normalizedCategory = category === 'unknown' ? 'log' : category;
  const safeParsedSourceCategory = sourceCategory === 'mood' ? 'note' : sourceCategory;
  const resolvedTitleSource = String(row?.title || docData.title || docData.titlePrefix || title).trim();
  const resolvedTitle = normalizedCategory === 'milestone'
    ? buildParsedSummaryTitle('milestone', resolvedTitleSource)
    : resolvedTitleSource;
  const resolvedTitlePrefixSource = String(row?.titlePrefix || docData.titlePrefix || docData.title || resolvedTitle || title).trim();
  const resolvedTitlePrefix = normalizedCategory === 'milestone'
    ? buildParsedSummaryTitle('milestone', resolvedTitlePrefixSource)
    : resolvedTitlePrefixSource;

  return {
    ...docData,
    category: normalizedCategory,
    type: normalizedCategory,
    logCategory: normalizedCategory,
    timelineType: normalizedCategory === 'behavior' ? 'incident' : 'dailyLog',
    entryType: normalizedCategory === 'behavior' ? 'incident' : 'note',
    parsedObservation: true,
    parsedSourceCategory: safeParsedSourceCategory,
    parsedSourceType: safeParsedSourceCategory,
    parsedSourceConfidence,
    title: resolvedTitle,
    titlePrefix: resolvedTitlePrefix,
    text: originalText || docData.text || fallbackText,
    content: originalText || docData.content || fallbackText,
    note: originalText || docData.note || fallbackText,
    notes: originalText || docData.notes || fallbackText,
    sourceText: originalText || docData.sourceText || docData.text || fallbackText,
    originalText: originalText || docData.originalText || docData.text || fallbackText,
    source: {
      ...(docData.source || {}),
      type: docData.source?.type || 'import',
      extractedBy: docData.source?.extractedBy || 'parser',
      confidence: parsedSourceConfidence,
      parsedObservation: true,
      parsedSourceCategory: safeParsedSourceCategory,
    },
  };
};

export const buildPreservedImportDocData = (row = {}, docData = {}) => {
  const sourceObject = normalizeSourceObject({
    ...(docData?.source || {}),
    ...(row?.source || {}),
  });
  const canonicalCategory = String(row?.category || docData?.category || 'log').trim().toLowerCase() || 'log';
  const canonicalText = String(
    row?.body
    || row?.note
    || row?.text
    || row?.content
    || row?.details
    || row?.summary
    || row?.value
    || docData?.body
    || docData?.note
    || docData?.text
    || docData?.content
    || docData?.details
    || docData?.summary
    || docData?.value
    || ''
  ).trim();
  const canonicalTitle = String(row?.title || row?.titlePrefix || docData?.title || docData?.titlePrefix || '').trim();
  const canonicalTitlePrefix = String(row?.titlePrefix || row?.title || docData?.titlePrefix || docData?.title || '').trim();
  const rawSourceText = String(row?.rawSourceText || row?.rawText || docData?.rawSourceText || docData?.rawText || canonicalText || '').trim();
  const sourceText = String(row?.sourceText || row?.originalText || docData?.sourceText || docData?.originalText || rawSourceText || canonicalText || '').trim();
  const originalText = String(row?.originalText || row?.sourceText || docData?.originalText || docData?.sourceText || sourceText || canonicalText || '').trim();
  const resolvedDate = row?.date || docData?.timestamp || docData?.date || new Date();
  const resolvedSourceMode = String(row?.sourceMode || row?.metadata?.sourceMode || sourceObject.mode || docData?.sourceMode || docData?.mode || 'unknown').trim() || 'unknown';
  const resolvedInputMode = String(row?.inputMode || row?.metadata?.inputMode || docData?.inputMode || docData?.mode || 'unknown').trim() || 'unknown';
  const resolvedParserMode = String(row?.parserMode || row?.metadata?.parserMode || docData?.parserMode || 'unknown').trim() || 'unknown';
  const isCaptureFirstRow = isCaptureFirstPreservedRow({
    row,
    docData,
    sourceObject,
    sourceMode: resolvedSourceMode,
    inputMode: resolvedInputMode,
    parserMode: resolvedParserMode,
  });
  const normalizedTimeSource = row?.timeSource || docData?.timeSource || 'missing';
  const resolvedDateObject = resolvedDate instanceof Date ? resolvedDate : new Date(resolvedDate);
  const safeDate = Number.isNaN(resolvedDateObject.getTime()) ? new Date() : resolvedDateObject;
  const resolvedParsedObservation = Boolean(row?.parsedObservation ?? docData?.parsedObservation ?? false);
  const resolvedParsedSourceCategory = String(row?.parsedSourceCategory || docData?.parsedSourceCategory || canonicalCategory).trim().toLowerCase() || canonicalCategory;
  const resolvedParsedSourceConfidence = row?.parsedSourceConfidence || row?.reviewConfidence || docData?.parsedSourceConfidence || docData?.reviewConfidence || 'low';
  const sourceNoteId = row?.sourceNoteId || row?.metadata?.sourceNoteId || docData?.sourceNoteId || docData?.metadata?.sourceNoteId || null;
  const sourceNotePath = row?.sourceNotePath || row?.metadata?.sourceNotePath || docData?.sourceNotePath || docData?.metadata?.sourceNotePath || '';
  const sourceNoteCollection = row?.sourceNoteCollection || row?.metadata?.sourceNoteCollection || docData?.sourceNoteCollection || docData?.metadata?.sourceNoteCollection || '';
  const sourceNoteType = row?.sourceNoteType || row?.metadata?.sourceNoteType || docData?.sourceNoteType || docData?.metadata?.sourceNoteType || '';
  const sourceNoteFields = sourceNoteId || sourceNotePath
    ? {
        sourceNoteId,
        sourceNotePath,
        sourceNoteCollection,
        sourceNoteType,
      }
    : {};
  const suspiciousPreservedTitle = isGenericOrSentenceLikeTitle(canonicalTitle, canonicalCategory, canonicalText)
    || isGenericOrSentenceLikeTitle(canonicalTitlePrefix, canonicalCategory, canonicalText);
  const categoryDecision = resolvedParsedObservation && !isCaptureFirstRow
    ? buildNaturalCaptureCategoryDecision(canonicalText, {
        currentPathCategory: canonicalCategory,
        categoryHint: resolvedParsedSourceCategory,
        sourceMode: resolvedSourceMode,
        inputMode: resolvedInputMode,
        parserMode: resolvedParserMode,
        shapeHint: 'preserved_import_doc',
        confidenceMetadata: { confidence: resolvedParsedSourceConfidence },
        categorySource: 'preserved_import_doc',
      })
    : null;
  const decisionDrivenCategory = (
    resolvedParsedObservation
    && suspiciousPreservedTitle
    && categoryDecision?.proposedCategory
    && normalizeImportCategory(categoryDecision.proposedCategory) !== normalizeImportCategory(canonicalCategory)
  )
    ? normalizeImportCategory(categoryDecision.proposedCategory)
    : normalizeImportCategory(categoryDecision?.finalCategory || canonicalCategory);
  const finalCategory = resolvedParsedObservation && !isCaptureFirstRow
    ? decisionDrivenCategory
    : canonicalCategory;
  const shouldRebuildTitle = resolvedParsedObservation && (
    finalCategory !== canonicalCategory
    || suspiciousPreservedTitle
  );
  const finalTitle = shouldRebuildTitle
    ? buildParsedSummaryTitle(finalCategory, canonicalText)
    : (canonicalTitle || canonicalText || '');
  const finalTitlePrefix = shouldRebuildTitle
    ? buildParsedSummaryTitle(finalCategory, canonicalText)
    : (canonicalTitlePrefix || canonicalTitle || canonicalText || '');
  const rowActivityTypes = Array.isArray(row?.activityTypes)
    ? row.activityTypes
    : Array.isArray(docData?.activityTypes)
      ? docData.activityTypes
      : String(row?.activityTypes || docData?.activityTypes || '').split(',').map((value) => value.trim()).filter(Boolean);

  return {
    ...docData,
    collection: docData.collection || 'dailyLogs',
    status: row?.status || docData.status || 'active',
    createdBy: docData.createdBy || null,
    createdAt: docData.createdAt || serverTimestamp(),
    updatedAt: docData.updatedAt || serverTimestamp(),
    timestamp: safeDate,
    entryDate: docData.entryDate || docData.entryDateLabel || safeDate.toDateString(),
    entryDayKey: docData.entryDayKey || null,
    category: finalCategory,
    type: finalCategory,
    logCategory: finalCategory,
    timelineType: row?.timelineType || docData.timelineType || '',
    entryType: row?.entryType || docData.entryType || '',
    title: finalTitle,
    titlePrefix: finalTitlePrefix,
    text: canonicalText || '',
    content: canonicalText || '',
    note: canonicalText || '',
    notes: canonicalText || '',
    body: canonicalText || '',
    description: String(row?.description || docData?.description || canonicalText || '').trim(),
    summary: String(row?.summary || docData?.summary || canonicalText || '').trim(),
    value: String(row?.value || docData?.value || canonicalText || '').trim(),
    sourceText,
    originalText,
    rawText: String(row?.rawText || docData?.rawText || rawSourceText || canonicalText || '').trim(),
    rawSourceText,
    ...sourceNoteFields,
    parserMode: resolvedParserMode,
    sourceMode: resolvedSourceMode,
    inputMode: resolvedInputMode,
    canonicalRowVersion: row?.canonicalRowVersion || row?.metadata?.canonicalRowVersion || docData?.canonicalRowVersion || docData?.metadata?.canonicalRowVersion || 1,
    localTime: normalizedTimeSource === 'explicit'
      ? (row?.localTime || docData?.localTime || formatLocalTimeForStorage(safeDate))
      : (row?.localTime || docData?.localTime || null),
    timeSource: normalizedTimeSource,
    timeBucket: row?.timeBucket || docData?.timeBucket || '',
    timeInferred: Boolean(row?.timeInferred ?? docData?.timeInferred),
    timeInferenceReason: row?.timeInferenceReason || docData?.timeInferenceReason || row?.timeBucket || docData?.timeBucket || '',
    authorId: docData.authorId || null,
    authorName: docData.authorName || 'User',
    authorEmail: docData.authorEmail || null,
    childId: docData.childId || row?.childId || null,
    importance: row?.importance === 'important' || docData.importance === 'important' ? 'important' : 'normal',
    severity: row?.severity || docData?.severity || '',
    severityLabel: row?.severityLabel || docData?.severityLabel || '',
    triggerSummary: row?.triggerSummary || docData?.triggerSummary || '',
    medicationName: row?.medicationName || docData?.medicationName || '',
    medicationScheduleDose: row?.medicationDose || docData?.medicationDose || '',
    medicationScheduleUnit: row?.medicationUnit || docData?.medicationUnit || '',
    medicationDetails: row?.medicationDetails || docData?.medicationDetails || null,
    foodDetails: row?.foodDetails || docData?.foodDetails || null,
    activityThemeLabel: row?.activityThemeLabel || docData?.activityThemeLabel || '',
    activityType: row?.activityType || docData?.activityType || '',
    activityTypes: rowActivityTypes,
    parsedObservation: resolvedParsedObservation,
    parsedSourceCategory: resolvedParsedSourceCategory,
    parsedSourceType: resolvedParsedSourceCategory,
    parsedSourceConfidence: resolvedParsedSourceConfidence,
    metadata: {
      ...(docData?.metadata || {}),
      ...(row?.metadata || {}),
      parserMode: resolvedParserMode,
      sourceMode: resolvedSourceMode,
      inputMode: resolvedInputMode,
      ...sourceNoteFields,
      canonicalRowVersion: row?.canonicalRowVersion || row?.metadata?.canonicalRowVersion || docData?.canonicalRowVersion || docData?.metadata?.canonicalRowVersion || 1,
      saveCanonicalCategory: finalCategory,
      saveOriginalCategory: canonicalCategory,
      categoryDecision: categoryDecision ? {
        finalCategory: categoryDecision.finalCategory,
        proposedCategory: categoryDecision.proposedCategory,
        originalCategory: categoryDecision.originalCategory,
        promotionApplied: categoryDecision.promotionApplied,
        promotionReason: categoryDecision.promotionReason,
      } : null,
    },
    source: {
      ...sourceObject,
      type: sourceObject.type || docData?.source?.type || 'import',
      mode: sourceObject.mode || resolvedSourceMode,
      parsedObservation: resolvedParsedObservation,
      parsedSourceCategory: resolvedParsedSourceCategory,
      confidence: resolvedParsedSourceConfidence,
      ...sourceNoteFields,
    },
    mode: docData.mode || resolvedInputMode,
  };
};
